import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { resolveAssetUrl } from "../../services/api";
import { EmployerHubLayout } from "../../components/hub/EmployerHubLayout";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, CalendarClock, CalendarCheck, FileText, Award as AwardIcon,
  ShieldCheck, MapPin, Clock, Globe2, BriefcaseBusiness, Sparkles,
} from "lucide-react";

const initialsOf = (name) =>
  (name || "").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

const Fact = ({ icon: Icon, label, value, testid }) =>
  value ? (
    <div className="flex items-start gap-2.5" data-testid={testid}>
      <Icon className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
      <div>
        <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
        <div className="text-sm text-gray-800">{value}</div>
      </div>
    </div>
  ) : null;

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-gray-500 mb-4">
    {Icon && <Icon className="h-4 w-4 text-blue-600" />}
    {children}
  </h2>
);

export default function EmployerHubCandidateReview() {
  const { assignmentId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/api/employer/hub/candidates/${assignmentId}`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.status === 404 ? "Candidate not found" : "Unable to load candidate"));
  }, [assignmentId]);

  const submitInterviewRequest = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/api/employer/hub/candidates/${assignmentId}/request-interview`, { message });
      setData((d) => ({ ...d, interview_requested_at: res.data.requested_at }));
      setDialogOpen(false);
      toast.success("Interview request sent");
    } catch (e) {
      const rawDetail = e.response?.data?.detail;
      const message = typeof rawDetail === "object" ? rawDetail?.message : rawDetail;
      toast.error(message || "Unable to send the request right now");
    } finally {
      setSubmitting(false);
    }
  };

  const c = data?.candidate;
  const a = data?.assignment;

  return (
    <EmployerHubLayout>
      <div data-testid="employer-hub-candidate-review">
        <Link
          to="/employers/hub/candidates"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 transition-colors"
          data-testid="review-back-link"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to candidates
        </Link>

        {error && (
          <div className="mt-8 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3" data-testid="review-error">
            {error}
          </div>
        )}

        {!data && !error && (
          <div className="mt-8 space-y-6" data-testid="review-loading">
            <div className="h-24 bg-gray-100 rounded-2xl animate-pulse" />
            <div className="h-80 bg-gray-100 rounded-2xl animate-pulse" />
          </div>
        )}

        {data && c && (
          <>
            {/* Identity header */}
            <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-6 sm:p-8" data-testid="review-identity">
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                {c.avatar_url ? (
                  <img src={resolveAssetUrl(c.avatar_url)} alt={c.candidate_name} className="h-20 w-20 rounded-full object-cover border border-gray-200" />
                ) : (
                  <span className="h-20 w-20 rounded-full bg-blue-600 text-white text-2xl font-semibold flex items-center justify-center shrink-0">
                    {initialsOf(c.candidate_name)}
                  </span>
                )}
                <div className="min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="review-candidate-name">
                    {c.candidate_name}
                  </h1>
                  {c.professional_title && (
                    <p className="text-gray-500 mt-1" data-testid="review-candidate-title">{c.professional_title}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    {c.city_region && (
                      <span className="inline-flex items-center gap-1 text-sm text-gray-400">
                        <MapPin className="h-3.5 w-3.5" /> {c.city_region}
                      </span>
                    )}
                    {a?.position_context && (
                      <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50 font-normal" data-testid="review-position-context">
                        For: {a.position_context}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              {c.bio && (
                <p className="mt-5 text-sm text-gray-600 leading-relaxed max-w-3xl" data-testid="review-candidate-bio">{c.bio}</p>
              )}
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left — introduction + materials + sections */}
              <div className="lg:col-span-2 space-y-6">
                {c.has_intro !== false && c.intro_url && (
                  <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid="review-intro-section">
                    <SectionTitle icon={Sparkles}>Personal introduction</SectionTitle>
                    {c.presentation_mode === "video" ? (
                      <video
                        controls
                        preload="metadata"
                        poster="/assets/intro-poster.png"
                        className="w-full rounded-xl border border-gray-200 bg-[#021423]"
                        src={resolveAssetUrl(c.intro_url)}
                        data-testid="review-video-player"
                      />
                    ) : (
                      <audio
                        controls
                        preload="metadata"
                        className="w-full"
                        src={resolveAssetUrl(c.intro_url)}
                        data-testid="review-audio-player"
                      />
                    )}
                  </div>
                )}

                {(c.resume_pdf_url || c.cover_letter_pdf_url) && (
                  <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid="review-materials-section">
                    <SectionTitle icon={FileText}>Application materials</SectionTitle>
                    <div className="flex flex-wrap gap-3">
                      {c.resume_pdf_url && (
                        <a href={resolveAssetUrl(c.resume_pdf_url)} target="_blank" rel="noreferrer">
                          <Button variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="review-view-resume">
                            <FileText className="h-4 w-4 mr-2" /> View Resume
                          </Button>
                        </a>
                      )}
                      {c.cover_letter_pdf_url && (
                        <a href={resolveAssetUrl(c.cover_letter_pdf_url)} target="_blank" rel="noreferrer">
                          <Button variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="review-view-cover-letter">
                            <FileText className="h-4 w-4 mr-2" /> View Cover Letter
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {(c.key_strengths || []).length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid="review-strengths-section">
                    <SectionTitle>Key strengths</SectionTitle>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {c.key_strengths.map((s, i) => (
                        <div key={i} className="rounded-xl bg-slate-50 border border-gray-100 p-4" data-testid={`review-strength-${i}`}>
                          <div className="text-sm font-semibold text-gray-900">{s.title}</div>
                          {s.description && <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">{s.description}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {((c.awards || []).length > 0 || (c.certifications || []).length > 0) && (
                  <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid="review-recognition-section">
                    {(c.awards || []).length > 0 && (
                      <div className="mb-6">
                        <SectionTitle icon={AwardIcon}>Awards</SectionTitle>
                        <ul className="space-y-2">
                          {c.awards.map((aw, i) => (
                            <li key={i} className="text-sm text-gray-700" data-testid={`review-award-${i}`}>
                              {aw.title}
                              {(aw.issuer || aw.year) && (
                                <span className="text-gray-400"> — {[aw.issuer, aw.year].filter(Boolean).join(", ")}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(c.certifications || []).length > 0 && (
                      <div>
                        <SectionTitle icon={ShieldCheck}>Certifications</SectionTitle>
                        <ul className="space-y-2">
                          {c.certifications.map((ct, i) => (
                            <li key={i} className="text-sm text-gray-700" data-testid={`review-certification-${i}`}>
                              {ct.name}
                              {(ct.issuer || ct.year) && (
                                <span className="text-gray-400"> — {[ct.issuer, ct.year].filter(Boolean).join(", ")}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right — action panel (rail sticks as one unit so the
                  action card can never overlap the facts card) */}
              <div className="space-y-6 lg:sticky lg:top-24 lg:self-start">
                <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid="review-action-panel">
                  {data.interview_requested_at ? (
                    <div data-testid="review-interview-requested-state">
                      <div className="flex items-center gap-2 text-emerald-700 font-medium">
                        <CalendarCheck className="h-5 w-5" /> Interview requested
                      </div>
                      <p className="mt-2 text-sm text-gray-500">
                        Sent {formatDate(data.interview_requested_at)}. {c.candidate_name.split(" ")[0]} will reply to you by email.
                      </p>
                    </div>
                  ) : (
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5" data-testid="review-request-interview-btn">
                          <CalendarClock className="h-4 w-4 mr-2" />
                          Request an Interview
                        </Button>
                      </DialogTrigger>
                      <DialogContent data-testid="review-interview-dialog">
                        <DialogHeader>
                          <DialogTitle>Request an interview with {c.candidate_name}</DialogTitle>
                          <DialogDescription>
                            Your request is delivered to {c.candidate_name.split(" ")[0]}&apos;s Inter-View inbox and email. They reply directly to you.
                          </DialogDescription>
                        </DialogHeader>
                        <Textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          maxLength={2000}
                          rows={4}
                          placeholder="Add a short note — when you'd like to meet, or what stood out to you. (optional)"
                          data-testid="review-interview-message"
                        />
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="review-interview-cancel">
                            Cancel
                          </Button>
                          <Button
                            onClick={submitInterviewRequest}
                            disabled={submitting}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid="review-interview-send"
                          >
                            {submitting ? "Sending…" : "Send request"}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                  {a?.assigned_at && (
                    <p className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400" data-testid="review-presented-date">
                      Presented to you {formatDate(a.assigned_at)}
                    </p>
                  )}
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4" data-testid="review-facts-panel">
                  <Fact icon={BriefcaseBusiness} label="Experience" value={c.years_of_experience ? `${c.years_of_experience} year${c.years_of_experience === 1 ? "" : "s"}` : null} testid="review-fact-experience" />
                  <Fact icon={Clock} label="Availability" value={c.availability} testid="review-fact-availability" />
                  <Fact icon={CalendarClock} label="Preference" value={c.employment_preference} testid="review-fact-preference" />
                  <Fact icon={Globe2} label="Languages" value={(c.languages_spoken || []).join(", ") || null} testid="review-fact-languages" />
                  <Fact icon={MapPin} label="Relocation" value={c.willingness_to_relocate} testid="review-fact-relocate" />
                  {c.availability_details && (
                    <div className="pt-3 border-t border-gray-100">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Schedule notes</div>
                      <p className="text-sm text-gray-600 leading-relaxed" data-testid="review-availability-details">{c.availability_details}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </EmployerHubLayout>
  );
}
